import { makeAssistantToolUI } from '@assistant-ui/react';
import { CheckCircle, ChevronDown, ChevronUp, CircleX, LoaderCircle } from 'lucide-react';
import { ToolSignature } from '@andsend/services/agent/shared';
import { useState, useRef, useEffect } from 'react';
import { CircleCheckAnimation } from '../../../action-feed/components/cards/circle-check-animation';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@shadcn/ui/components/ui/tooltip';
import { Button } from '@shadcn/ui/components/ui/button';
import { cn } from '@shadcn/ui/lib/utils';
/**
 * Creates a generic tool UI component for a tool signature that includes a toolCallUi function.
 *
 * @param toolSignature The tool signature that includes a toolCallUi function
 * @returns An AssistantToolUI component for the tool
 */
export const createGenericToolUI = (toolSignature: ToolSignature) => {
    return makeAssistantToolUI({
        toolName: toolSignature.name,
        render: ({ args, result, status }) => {
            return <GenericToolUI toolSignature={toolSignature} args={args} status={status} />;
        },
    });
};

export const GenericToolUI = ({
    toolSignature,
    args,
    status,
    children: expandableChildren,
}: {
    toolSignature: ToolSignature;
    args: any;
    status: any;
    children?: React.ReactNode;
}) => {
    const toolCallUiMessage = toolSignature.toolCallUi(args);
    const isExpandable = Boolean(expandableChildren);
    const [isExpanded, setIsExpanded] = useState(false);

    const isRunning = status.type === 'running';
    const isComplete = status.type === 'complete';

    return (
        <>
            <Button
                variant="ghost"
                // disabled={expandOptions === undefined}
                onClick={isExpandable ? () => setIsExpanded(!isExpanded) : undefined}
                className={cn(
                    'w-full hover:bg-none p-0 hover:bg-background-secondary',
                    !isExpandable && 'cursor-auto hover:bg-background-primary'
                )}
            >
                <div className="flex w-full h-full flex-row items-center justify-between">
                    <div className="flex-1 overflow-hidden text-left pr-2 min-w-0">
                        <div className="flex flex-row items-center gap-2 py-1 w-full overflow-hidden">
                            {isRunning ? (
                                <LoaderCircle className="w-4 h-4 animate-spin shrink-0" />
                            ) : isComplete ? (
                                <CircleCheckAnimation className="w-4 h-4 shrink-0" />
                            ) : (
                                <CircleX className="w-4 h-4 shrink-0" />
                            )}
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <p className="w-full line-clamp-1 whitespace-pre-line truncate">
                                            {toolCallUiMessage}
                                        </p>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-[400px] text-left whitespace-pre-line break-words">
                                        {toolCallUiMessage}
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                    </div>
                    {isExpandable && (
                        <>
                            {isExpanded ? (
                                <ChevronUp className="h-4 w-4 shrink-0 ml-2" />
                            ) : (
                                <ChevronDown className="h-4 w-4 shrink-0 ml-2" />
                            )}
                        </>
                    )}
                </div>
            </Button>
            {isExpandable && isExpanded && expandableChildren}
        </>
    );
};
